import i18next from 'i18next';
import common from '../common';
import Text from './Text';

export default class EarlyWarningSystem extends PIXI.Container {
	constructor() {
		super();

		this.visible = true;
		this._timer = 60;
		this._countdownTime = Date.now();

		this._world = null;
		this._communityCenter = null;
		this._extended = false;
		this._sirenSFX = null;

		this._destroyed = false;
		this._tweening = false;

		this.signals = {
			evacClick: new P3.utils.MiniSignal(),
		};

		this.init();
	}

	init() {
		this.panel = this.createPanel();
		this.panel.y = -120;
		this.addChild(this.panel);
	}

	destroy() {
		P3.utils.destroyAllSignals(this.signals);
		this._destroyed = true;
		(this._sirenSFX && this._sirenSFX.howl) && this._sirenSFX.destroy();
		super.destroy();
	}

	createTopbar() {
		const bar = new PIXI.Container();

		bar.bg = new PIXI.Sprite(P3.assets.texture('early_warning_top_bar'));
		bar.bg.anchor.set(0.5, 0);
		bar.bg.position.set(-(bar.width * 0.5), 0);
		bar.addChild(bar.bg);

		bar.label = new Text('gui.general.ews.title', { font: '24px', align: 'left', tint: 0xFFFFFF });
		bar.label.anchor.set(0.5);
		bar.label.position.set(0, bar.height * 0.5);
		bar.addChild(bar.label);

		return bar;
	}

	createPanel() {
		const panel = new PIXI.Container();

		panel.bg = new PIXI.Sprite(P3.assets.texture('panel_early_warning_system'));
		panel.bg.anchor.set(0.5, 0);
		panel.bg.position.set(-(panel.width * 0.5), 0);
		panel.addChild(panel.bg);

		panel.label = new Text('gui.general.ews.title', { font: '32px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF });
		panel.label.anchor.set(0.5);
		panel.label.position.set(0, 17);
		panel.addChild(panel.label);

		panel.page1 = new PIXI.Container();
		panel.addChild(panel.page1);

		panel.page1.ewsButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_early_warning_9slice_out', width: 250, height: 60, left: 30, top: 30, right: 30, bottom: 30 },
			over: { textureName: 'btn_early_warning_9slice_over', width: 250, height: 60, left: 30, top: 30, right: 30, bottom: 30 },
		});

		panel.page1.ewsButton.signals.click.add(this.onEvacuateClick, this);
		panel.page1.y = panel.height * 0.5 + 30;
		panel.page1.addChild(panel.page1.ewsButton);

		panel.page1.ewsButton.label = new Text('gui.general.ews.button', { font: '32px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF });
		panel.page1.ewsButton.label.anchor.set(0.5);
		panel.page1.ewsButton.label.position.set(0, -5);
		panel.page1.ewsButton.addChild(panel.page1.ewsButton.label);

		panel.page1.ewsButton.accessibleTitle = panel.page1.ewsButton.label.text;

		panel.page1.timer = new Text(this.displaySeconds(60), { font: '38px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF });
		panel.page1.timer.anchor.set(0.5);
		panel.page1.timer.position.set(0, -50);
		panel.page1.addChild(panel.page1.timer);

		panel.page1.disasterIconL = new PIXI.Sprite(P3.assets.texture(`icon_${common.selectedLevel}`));
		panel.page1.disasterIconL.anchor.set(0.5);
		panel.page1.disasterIconL.scale.set(0.4);
		panel.page1.disasterIconL.position.set(-160, panel.page1.ewsButton.y - 5);
		panel.page1.addChild(panel.page1.disasterIconL);

		panel.page1.disasterIconR = new PIXI.Sprite(P3.assets.texture(`icon_${common.selectedLevel}`));
		panel.page1.disasterIconR.anchor.set(0.5);
		panel.page1.disasterIconR.scale.set(0.4);
		panel.page1.disasterIconR.position.set(160, panel.page1.ewsButton.y - 5);
		panel.page1.addChild(panel.page1.disasterIconR);

		panel.page2 = new PIXI.Container();
		panel.page2.alpha = 0;
		panel.addChild(panel.page2);

		let text = '';

		if (common.motion) {
			text = `${i18next.t('gui.general.ews.evacuating')} ${i18next.t('gui.general.ews.aids')} `;
		} else {
			text = `${i18next.t('gui.general.ews.evacuating')}`;
		}

		panel.page2.scrollingText = new Text(text, { font: '26px', align: 'left', tint: 0xFFFFFF });
		panel.page2.scrollingText.signals.languageChange.add(() => {
			this.updateEvacText();
		}, this);
		panel.page2.scrollingText.anchor.set(0, 0.5);
		panel.page2.scrollingText.position.set(panel.width * 0.5, panel.height - 28);
		panel.page2.addChild(panel.page2.scrollingText);

		panel.page2.scrollingTextSecond = new Text(text, { font: '26px', align: 'left', tint: 0xFFFFFF });
		panel.page2.scrollingTextSecond.anchor.set(0, 0.5);
		panel.page2.scrollingTextSecond.position.set(panel.page2.scrollingText.x + panel.page2.scrollingText.width + 20, panel.height - 28);
		panel.page2.addChild(panel.page2.scrollingTextSecond);

		panel.page2.textMask = new PIXI.Graphics();
		panel.page2.textMask.beginFill(0x00FF00, 0.6);
		panel.page2.textMask.drawRect(-(panel.bg.width * 0.5) + 10, 0, panel.bg.width - 20, panel.bg.height);
		panel.page2.textMask.endFill();
		panel.page2.addChild(panel.page2.textMask);
		panel.page2.scrollingText.mask = panel.page2.textMask;
		panel.page2.scrollingTextSecond.mask = panel.page2.textMask;

		if (!common.motion) {
			panel.page2.scrollingTextSecond.visible = false;
			panel.page2.scrollingText.position.set(panel.page2.scrollingText.width * -0.5, panel.height - 28);
		}

		return panel;
	}

	update() {
		if (this._destroyed) {
			return;
		}

		this._timer -= window.dt;
		this.panel.page1.timer.text = this.displaySeconds(Math.max(0, this._timer));

		if (this._timer <= 0 && !this._tweening && !common.scenario.disaster.isRunning) {
			this._sirenSFX && this._sirenSFX.stop();
			common.scenario.disaster.start();
			this.hide();
		}

		if (this.panel.page2.alpha !== 0 && common.motion) {
			this.panel.page2.scrollingText.x -= 50 * window.dt;
			this.panel.page2.scrollingTextSecond.x -= 50 * window.dt;

			if (this.panel.page2.scrollingText.x + this.panel.page2.scrollingText.width < this.panel.bg.x - this.panel.bg.width * 0.5) {
				this.panel.page2.scrollingText.x = this.panel.page2.scrollingTextSecond.x + this.panel.page2.scrollingTextSecond.width + 20;
			}
			if (this.panel.page2.scrollingTextSecond.x + this.panel.page2.scrollingTextSecond.width < this.panel.bg.x - this.panel.bg.width * 0.5) {
				this.panel.page2.scrollingTextSecond.x = this.panel.page2.scrollingText.x + this.panel.page2.scrollingText.width + 20;
			}
		}
	}

	displaySeconds(time) {
		const m = Math.floor(time / 60);
		const s = P3.utils.padNum(Math.floor(time % 60), 2);

		return `${m}:${s}`;
	}

	appear() {
		if (this._extended) {
			return;
		}
		this.visible = true;
		this.updateEvacText();

		this._tweening = true;
		const t1 = new TWEEN.Tween(this, this.tweens)
			.to({
				y: '+147',
			}, 1500)
			.easing(TWEEN.Easing.Quadratic.Out)
			.onComplete(() => {
				this._tweening = false;
				this._extended = true;
			});

		t1.start();
	}

	hide() {
		if (this._tweening) {
			return;
		}

		this._tweening = true;
		const t1 = new TWEEN.Tween(this.panel, this.tweens)
			.to({
				y: '-147',
			}, 1500)
			.easing(TWEEN.Easing.Quadratic.Out)
			.onComplete(() => {
				this._tweening = false;
				this.visible = false;
			});

		t1.start();
	}

	onEvacuateClick() {
		this.signals.evacClick.dispatch();

		this._sirenSFX = P3.audio.playSound('sfx_siren', { loop: true });
		const t1 = new TWEEN.Tween(this.panel, this.tweens)
			.to({
				y: '-110',
			}, 1000)
			.easing(TWEEN.Easing.Quadratic.Out);

		const t2 = new TWEEN.Tween(this.panel.page1, this.tweens)
			.to({
				alpha: 0,
			}, 500)
			.easing(TWEEN.Easing.Quadratic.Out);

		const t3 = new TWEEN.Tween(this.panel.page2, this.tweens)
			.to({
				alpha: 1,
			}, 500)
			.easing(TWEEN.Easing.Quadratic.Out)
			.onComplete(() => {
				this.panel.page1.visible = false;
				this.panel.page2.visible = true;
				this._timer = 30;
				this._extended = true;
			}, this);

		t1.start();
		t2.chain(t3);
		t2.start();
	}

	updateEvacText() {
		if (!common.motion) {
			return;
		}

		if (common.scenario._ccUpgrades.length <= 0) {
			const text = `${i18next.t('gui.general.ews.evacuating')} ${i18next.t('gui.general.ews.noaids')} `;

			this.panel.page2.scrollingText.text = text;
			this.panel.page2.scrollingTextSecond.text = text;

			return;
		}

		let text = `${i18next.t('gui.general.ews.evacuating')} ${i18next.t('gui.general.ews.aids')} `;

		for (const u of common.scenario._ccUpgrades) {
			text += `${i18next.t(this._world.getUpgrade(u).description)}, `;
		}

		this.panel.page2.scrollingText.text = text;
		this.panel.page2.scrollingTextSecond.text = text;
		this.panel.page2.scrollingTextSecond.x = this.panel.page2.scrollingText.x + this.panel.page2.scrollingText.width + 20;
	}

	set world(v) {
		this._world = v;
	}

	get world() {
		return this._world;
	}

	set communityCenter(v) {
		this._communityCenter = v;
	}

	get communityCenter() {
		return this._communityCenter;
	}

	get extended() {
		return this._extended;
	}
}
